import { useCallback, useEffect, useMemo, useState } from 'react';
import fetchGraphql, { GraphQLError, Extensions } from './fetchGraphql';

type CallbackOptions<Query> = {
    /** @deprecated will be phased out once migrated away from apollo */
    onCompleted?: (data: Query) => void;
    /** @deprecated will be phased out once migrated away from apollo */
    onError?: (errors?: GraphQLError[] | NetworkError) => void;
};

type UseLazyFetchReturn<Query, Variables> = {
    lazyFetch: (Variables?: Variables) => Promise<{
        data?: Query;
        errors?: GraphQLError[];
        extensions?: Extensions;
    }>;
    isPending: boolean;
    data?: Query;
    graphqlError?: GraphQLError[];
};

class NetworkError extends Error {
    isNetworkError = true;
}

const useLazyFetchGraphql = <Query, Variables = Record<string, unknown>>(
    query: string,
    { onCompleted, onError }: CallbackOptions<Query> = {}
): UseLazyFetchReturn<Query, Variables> => {
    const [isPending, setIsPending] = useState(false);
    const [data, setData] = useState<Query>();
    const [graphqlError, setGraphqlError] = useState<GraphQLError[]>();

    const controller = useMemo(() => new AbortController(), []);

    useEffect(() => {
        return () => {
            controller.abort();
            setIsPending(false);
        };
    }, [controller]);

    const lazyFetch = useCallback(
        (variables: Variables | undefined) => {
            const fetchData = async () => {
                try {
                    setIsPending(true);

                    const options = { signal: controller.signal };

                    const { data, errors, extensions } = await fetchGraphql<
                        Query,
                        Variables
                    >(query, variables, options);

                    if (typeof onCompleted === 'function') {
                        // TODO: Allow onCompleted to be called without data
                        if (data) onCompleted(data);
                    }

                    setGraphqlError(() => {
                        if (!errors) {
                            return;
                        }
                        if (typeof onError === 'function') {
                            onError(errors);
                        }
                        return errors;
                    });

                    setData(data || undefined);
                    setIsPending(false);

                    return { data, errors, extensions };
                } catch (error) {
                    if (
                        !controller.signal.aborted &&
                        typeof onError === 'function'
                    ) {
                        // this will be hit only on a network problem
                        onError(new NetworkError());
                    }
                }

                if (!controller.signal.aborted) {
                    setIsPending(false);
                }

                return { data, graphqlError };
            };

            return fetchData();
        },
        [controller.signal, data, graphqlError, onCompleted, onError, query]
    );

    return { lazyFetch, isPending, data, graphqlError };
};

export { useLazyFetchGraphql };
